/**
 * MultiSiteAccountsCard (Iter174) — surfaces owners who show up in
 * multiple parcels across our Smith universe. High-value cross-sell
 * signal: a single master security contract with the parent entity can
 * wrap N sites with uniform monitoring, consolidated dashboards, and a
 * shared responder radius.
 *
 * Renders on the Smith Dashboard right column below the standard stats.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Layers3, Building2, MapPin, TrendingUp, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => (n ? `$${Math.round(n).toLocaleString("en-US")}` : "—");
const sqft = (n) => (n ? `${Math.round(n).toLocaleString("en-US")} sqft` : "—");

export default function MultiSiteAccountsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/smith/analytics/multi-site?min_sites=2&limit=10`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="rounded-2xl border p-4 sm:p-5 space-y-3"
      data-testid="multi-site-card"
      style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(139,92,246,0.25)" }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers3 className="w-4 h-4 text-[#A78BFA]" />
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-[#A78BFA] font-bold">
              Multi-Site Accounts
            </div>
            <div className="text-[11px] text-slate-400">
              Master-contract opportunities — one entity, multiple parcels
            </div>
          </div>
        </div>
        {items.length > 0 && (
          <span className="text-[11px] font-bold text-slate-300 tabular-nums">
            {items.length} owners
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-[12px] py-4" data-testid="multi-site-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Analisando universo…
        </div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-[12px] italic py-3" data-testid="multi-site-empty">
          Nenhum owner com múltiplos sites ainda. Rode o PA scraper em mais condados.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="multi-site-list">
          {items.map((it) => (
            <li
              key={it.owner_key}
              data-testid={`multi-site-row-${it.owner_key.slice(0, 32)}`}
              className="rounded-lg border p-2.5 hover:border-[#A78BFA]/50 transition-colors"
              style={{ borderColor: "rgba(139,92,246,0.15)", background: "rgba(139,92,246,0.05)" }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-bold text-white truncate max-w-[260px]" title={it.display_name}>
                      {it.display_name}
                    </span>
                    <span className="text-[9.5px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={{ background: "#A78BFA22", color: "#A78BFA" }}>
                      {it.sites} sites
                    </span>
                    {it.counties?.length > 1 && (
                      <span className="text-[9.5px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ background: "#F59E0B22", color: "#F59E0B" }}
                            title="Cross-county">
                        Cross-county
                      </span>
                    )}
                    {it.states?.length > 1 && (
                      <span className="text-[9.5px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ background: "#DC262622", color: "#F87171" }}>
                        Multi-state
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-300 font-bold tabular-nums">{usd(it.total_acv)}</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      <span className="text-slate-200 tabular-nums">{sqft(it.total_sqft)}</span>
                    </span>
                    {it.cities?.length > 0 && (
                      <span className="inline-flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[220px]" title={it.cities.join(", ")}>
                          {it.cities.slice(0, 3).join(", ")}
                          {it.cities.length > 3 ? ` +${it.cities.length - 3}` : ""}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={`/smith/prospects?q=${encodeURIComponent(it.display_name)}`}
                  data-testid={`multi-site-open-${it.owner_key.slice(0, 32)}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-bold text-[#A78BFA] hover:text-white hover:bg-[#A78BFA]/20"
                  title="Ver todos os parcéis deste owner"
                >
                  Ver
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
